'use client';

import { MouseEvent, useEffect, useRef, useState } from 'react';

import './css/modal.css';

type RenderFunction = (handleClose: () => void) => React.ReactNode;

interface IModalProps {
  isOpen: boolean;
  className?: string;
  closeModal: () => void;
  toggleModal?: () => void;
  displayCloseButton?: boolean;
  closeButtonClassName?: string;
  children: RenderFunction;
}

const delay = 200;

export const Modal = ({ isOpen, closeModal, className, displayCloseButton = false, children }: IModalProps) => {
  const [isDisplayed, setIsDisplayed] = useState<boolean>(isOpen);
  const modalRef = useRef<HTMLDivElement | null>(null);

  const handleClose = (event: MouseEvent<HTMLDivElement> | null = null) => {
    if (event) {
      const target = event.target as HTMLElement;
      const isModalContent = target.closest('.modal__content');
      const isCloseButton = target.closest('.modal__close');

      if (!modalRef.current || (!isCloseButton && isModalContent)) {
        return;
      }
    }
    modalRef.current?.classList.remove('modal-open');
    document.body.style.overflow = '';
    setTimeout(() => {
      closeModal();
    }, delay);
  };

  useEffect(() => {
    if (isOpen) {
      setIsDisplayed(isOpen);
    } else {
      document.body.style.overflow = '';
      if (modalRef.current) modalRef.current?.classList.remove('modal-open');
      setTimeout(() => {
        setIsDisplayed(isOpen);
      }, delay);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isDisplayed) {
      document.body.style.overflow = 'hidden';
      setTimeout(() => {
        if (modalRef.current) modalRef.current.classList.add('modal-open');
      }, 0);
    }

    if (!isDisplayed) {
      document.body.style.overflow = '';
      if (modalRef.current) modalRef.current?.classList.remove('modal-open');
      setTimeout(() => {
        closeModal();
      }, delay);
    }
  }, [isDisplayed]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  if (!isDisplayed) return null;

  return (
    <div
      ref={modalRef}
      className="fixed modal__container w-[100vw] flex flex-col items-center justify-center inset-0"
      onMouseDown={handleClose}
    >
      <div className={`${className || ''} modal__content`}>{children(handleClose)}</div>
    </div>
  );
};
